import {html} from 'lit';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';

export interface FilterItemInterface {
    label: string;
    href: string;
    selectorPrefix: string;
}

export interface FilterGroupInterface {
    filterLabel: string;
    filters: FilterItemInterface[];
    currentGroupFilterLabels: string[];
}


export interface FiltersInterface {
    displayType: 'buttons' | 'select' | 'multipleFilters';
    filters: FilterItemInterface[];
    filterGroup: FilterGroupInterface[];
    selectLabel?: string;
    currentFilterLabels?: string[];
    filterClass?: string;
}

export class Filters {
    static readonly DEFAULT_LABEL = 'Select';

    constructor(props?: FiltersInterface) {
        this._displayType = props?.displayType;
        this._filters = props?.filters;
        this._filterGroup = props?.filterGroup;
        this._selectLabel = props?.selectLabel || Filters.DEFAULT_LABEL;
        this._currentFilterLabels = props?.currentFilterLabels || [];
        this._class = props?.filterClass ?? "";
    }

    private _displayType?: string;
    private _filters?: FilterItemInterface[];
    private _filterGroup?: FilterGroupInterface[];
    private _selectLabel?: string;
    private _currentFilterLabels?: string[];
    private _class?: string;

    private renderFilterButtons = () => {
        let filterButtons = '';
        for (let idx = 0; idx < (this._filters || []).length; idx++) {
            const filterItem = this._filters![idx];
            let filterClass = '';
            let filterDisabled = '';
            let filterHref = filterItem.href;

            for (let fl = 0; fl < (this._currentFilterLabels || []).length; fl++) {

                const currentActiveFilterLabel = this._currentFilterLabels![fl];
                if (filterItem.label === currentActiveFilterLabel) {
                    filterClass = ` active`;
                    filterHref = window.location.pathname;
                }
            }
            filterButtons += `<sl-button size='medium' href='${filterHref}' pill class='${filterClass} '${filterDisabled} >${filterItem.label}</sl-button>`
        }
        return html`${unsafeHTML(filterButtons)}`;
    }

    private renderFilterSelect = () => {
        let filterSelect = '';
        for (let idx = 0; idx < (this._filters || []).length; idx++) {
            const filterItem = this._filters![idx];
            let filterClass = '';
            let filterOptions = '';

            for (let fl = 0; fl < (this._currentFilterLabels || []).length; fl++) {

                const currentActiveFilterLabel = this._currentFilterLabels![fl].toLowerCase();
                if (filterItem.label.toLowerCase() === currentActiveFilterLabel) {
                    filterClass = ` active`;
                    filterOptions += ` type='checkbox'`
                    filterOptions += ' disabled';
                    filterOptions += ' checked';
                }
            }
            filterSelect += `<sl-menu-item class='${filterClass}'${filterOptions} value='${filterItem.href.toLowerCase()}'>${filterItem.label}</sl-menu-item>`
        }
        return html`${unsafeHTML(filterSelect)}`;
    }

    private renderMultipleFilters = () => {
        if (!this._filterGroup) return html``;

        const originalLabels = this._currentFilterLabels ?? [];
        const cleanedFilterLabels: string[] = [];

        let isPreviousGroupActive = false;

        for (let i = 0; i < this._filterGroup.length; i++) {
            const group = this._filterGroup[i];
            const groupHrefs = group.filters.map(f => f.href.toLowerCase());

            if (i === 0 || isPreviousGroupActive) {
                const activeInGroup = groupHrefs.filter(href => originalLabels.includes(href.toLowerCase()));
                cleanedFilterLabels.push(...activeInGroup);
                isPreviousGroupActive = activeInGroup.length > 0;
            } else {
                break;
            }
        }

        const currentFilterLabels = cleanedFilterLabels;
        let isPreviousFilterActive = false;
        let buttonGroupHtml = '';

        this._filterGroup.forEach((group, groupIndex) => {
            const groupHrefs = group.filters.map(f => f.href.toLowerCase());
            const isGroupHasActiveLabel = groupHrefs.some(href => currentFilterLabels.includes(href.toLowerCase()));
            let selectorPrefix = '';

            const filterButtonsHtml = group.filters.map(filterItem => {
                const isActive = currentFilterLabels.includes(filterItem.href.toLowerCase());
                const isHidden = !isActive && isGroupHasActiveLabel;
                const filterClass = `${isActive ? 'active' : ''} ${isHidden ? 'hidden' : ''}`.trim();
                const filterButtonSize = isActive ? 'small' : 'medium';

                let hrefFilterList = [...currentFilterLabels];

                if (isActive) {
                    selectorPrefix = filterItem.selectorPrefix;
                    hrefFilterList = hrefFilterList.filter(href => href !== filterItem.href.toLowerCase());
                } else {
                    hrefFilterList.push(filterItem.href.toLowerCase());
                }

                return `<sl-button size='${filterButtonSize}' prefix="${filterItem.selectorPrefix}" filter='${filterItem.href}' pill class='bg-advanced-filter-option ${filterClass}' filter-group="${groupIndex}">${filterItem.label}</sl-button>`;
            }).join('');
            

            if (selectorPrefix !='') {
                selectorPrefix = group.filters[0]?.selectorPrefix;
            }

            const groupClass = `bg-document-list-multiple-filter ${isGroupHasActiveLabel ? "active-group-row" : ""} ${isPreviousFilterActive || groupIndex === 0 ? "" : "hidden"}`;
            const questionNumberClass = `bg-document-list-multiple-filter-question-number ${!isGroupHasActiveLabel ? "active" : ""}`;
            const pattern = /\{[^{}]+\}/g;
            let filterLabelOriginalText = group.filterLabel;
            const filterLabel = filterLabelOriginalText.replace(pattern, selectorPrefix);

            buttonGroupHtml += `
            <div class="${groupClass}">
                <div class="bg-document-list-multiple-filter-label">
                    <div class="${questionNumberClass}">${groupIndex + 1}</div>
                    <div class="bg-document-library-filter-group-label">${filterLabel}</div>
                </div>
                <sl-button-group label="Filters">${filterButtonsHtml}</sl-button-group>
            </div>
        `;

            isPreviousFilterActive = isGroupHasActiveLabel;
        });
        return html`
            <div class="bg-document-list-multiple-filters-container bg-document-list-multiple-filter-desktop">
                ${unsafeHTML(buttonGroupHtml)}
            </div>
            <div class="bg-document-list-multiple-filter-mobile">
            </div>`;
    };


    renderFilters = () => {
        if (this._displayType === 'multipleFilters') {
            if (!this._filterGroup || this._filterGroup.length === 0) {
                return html``;
            }
        } else {
            if (!this._filters || this._filters.length === 0) {
                return html``;
            }
        }
        let filterResult = html``;
        if (this._displayType === 'buttons') {
            filterResult = html`
                <sl-button-group label="Filters">${this.renderFilterButtons()}</sl-button-group>`;
        } else if (this._displayType === 'select') {
            filterResult = html`
                <sl-dropdown>
                    <sl-button slot="trigger" caret>${this._selectLabel}</sl-button>
                    <sl-menu>${this.renderFilterSelect()}</sl-menu>
                </sl-dropdown>`;
        } else if (this._displayType === 'multipleFilters') {
            this._class = this._class + " bg-multi-filters";
            filterResult = html`
                ${this.renderMultipleFilters()}`;
        }

        return html`
            <div class="bg-filters ${this._class}">${filterResult}</div>`;
    }
}
